import { FileSelectionMethods, UploadedFile } from '@wix/chat-web';
import { useServices } from './services-registry';
import { useAppState } from './app-state';
import { mediaManager } from '@wix/inbox-common';
import { IMAGE_PREVIEW_MODAL_SIZE } from '../constants';

export const useImageHandlers = () => {
  const { appState } = useAppState();
  const { serverApi, hostSdk, biLogger, experiments } = useServices();

  const handleDownloadFile = async (fileId?: string): Promise<string> => {
    const isHipaaComplianceEnabled = await serverApi.isHipaaComplianceEnabled(
      hostSdk.getMetaSiteId(),
    );

    const useMediaV2 =
      experiments.enabled('specs.inbox.UseMediaV2') || isHipaaComplianceEnabled;

    if (!useMediaV2) {
      return '';
    }

    const response = await serverApi.generateFileDownloadUrl(fileId);

    return response?.downloadUrl ?? '';
  };

  const handleUploadFile = async (
    file: File,
    source: FileSelectionMethods,
  ): Promise<UploadedFile> => {
    const metaSiteId = hostSdk.getMetaSiteId();
    const isHipaaComplianceEnabled = await serverApi.isHipaaComplianceEnabled(
      metaSiteId,
    );
    const useMediaV2 =
      experiments.enabled('specs.inbox.UseMediaV2') || isHipaaComplianceEnabled;

    if (useMediaV2) {
      const uploadRes = await serverApi.generateFileUploadUrl(
        file.name,
        file.type,
      );
      if (!uploadRes?.uploadUrl) {
        throw new Error('Failed to generate upload URL');
      }
      const response = await serverApi.uploadFileToSecureUrl(
        uploadRes.uploadUrl,
        file.type,
        file,
      );
      await new Promise((resolve) => setTimeout(resolve, 2000));
      return {
        name: response?.name,
        mediaFileId: response?.mediaFileId || file.name,
        url: response.thumbnailUrl || response.url,
        thumbnailUrl: response?.thumbnailUrl,
        contentType: response?.contentType,
        fileSize: response.fileSize,
        mediaProvider: response?.mediaProvider,
        width: response.width,
        height: response.height,
      };
    }

    const { token, url, folderId } = await serverApi.getUploadToken();
    const effectiveFolderId = folderId;

    if (source !== FileSelectionMethods.Input) {
      const { chatroomId, visitorId } = appState;
      biLogger.fileUploadFromClipboard(
        file.name,
        file.size,
        file.type,
        '',
        chatroomId,
        visitorId,
        source === FileSelectionMethods.DragNDrop,
      );
    }

    const response = await mediaManager.uploadFileToMedia({
      file,
      token,
      uploadUrlApi: url,
      folderId: effectiveFolderId,
    });
    return response;
  };

  const handleImageClick = ({
    url,
    width,
    height,
    fileName,
    fileSize,
    fileId,
  }) => {
    const { deviceType } = appState;

    // TODO use correct wiring here
    const buttonBackground = 'black';
    const textColor = 'black';

    const isDesktop = deviceType === 'desktop';
    biLogger.imageClickEvent();
    hostSdk.openImagePreview(
      url,
      fileName,
      fileSize,
      buttonBackground,
      '',
      textColor,
      isDesktop ? IMAGE_PREVIEW_MODAL_SIZE.width : undefined,
      isDesktop ? IMAGE_PREVIEW_MODAL_SIZE.height : undefined,
      fileId,
    );
  };

  return { handleImageClick, handleUploadFile, handleDownloadFile };
};
